import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaClock,
  FaUser,
  FaBuilding,
  FaMicrophone,
  FaSearch,
  FaEye,
  FaTimes
} from "react-icons/fa";

const RejectedFreeConference = () => {
  const [conferences, setConferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [selectedConference, setSelectedConference] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Search/filter states
  const [searchText, setSearchText] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [todayOnly, setTodayOnly] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 15;

  useEffect(() => {
    const fetchConferences = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/press/free-conference/admin/all`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        console.log("Fetched rejected Conferences:", response.data);
        const conferencesData = response.data.data?.conferences || response.data.conferences || [];
        const sortedData = [...conferencesData].sort((a, b) => {
          const dateA = new Date(a.adminAction?.actionDate || a.createdAt || 0);
          const dateB = new Date(b.adminAction?.actionDate || b.createdAt || 0);
          return dateB - dateA;
        });
        setConferences(sortedData);
      } catch (err) {
        setError("Failed to fetch conferences");
      } finally {
        setLoading(false);
      }
    };
    fetchConferences();
  }, []);

  // Set startDate and endDate to today when todayOnly is checked
  useEffect(() => {
    if (todayOnly) {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      const todayStr = `${yyyy}-${mm}-${dd}`;
      setStartDate(todayStr);
      setEndDate(todayStr);
    }
  }, [todayOnly]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, startDate, endDate, todayOnly]);

  // Date helper function
  const toDate = (dateStr, endOfDay = false) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    if (endOfDay) {
      d.setHours(23, 59, 59, 999);
    } else {
      d.setHours(0, 0, 0, 0);
    }
    return d;
  };



  // Filtered conferences based on search and date filters
  const filteredConferences = conferences.filter((conference) => {
    // Status filter - only show rejected conferences
    if (conference.status !== "rejected") return false;

    // Search filter
    const q = searchText.trim().toLowerCase();
    if (q) {
      // Check conferenceId, state, or city
      if (conference.conferenceId && conference.conferenceId.toString().toLowerCase().includes(q)) return true;
      if (conference.state && conference.state.toLowerCase().includes(q)) return true;
      if (conference.city && conference.city.toLowerCase().includes(q)) return true;
      if (conference.submittedBy?.name && conference.submittedBy.name.toLowerCase().includes(q)) return true;
      return false;
    }

    // Date filter
    const conferenceDate = toDate(conference.conferenceDate);
    const filterStart = toDate(startDate);
    const filterEnd = toDate(endDate, true);

    // Skip conferences with invalid dates if filters are active
    if ((todayOnly || filterStart || filterEnd) && !conferenceDate) {
      return false;
    }

    // Apply date filters
    if (filterStart && conferenceDate < filterStart) return false;
    if (filterEnd && conferenceDate > filterEnd) return false;

    return true;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredConferences.length / rowsPerPage);
  const paginatedConferences = filteredConferences.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Handle view details
  const handleViewDetails = (conference) => {
    setSelectedConference(conference);
    setShowModal(true);
  };

  // Handle close modal
  const handleCloseModal = () => {
    setSelectedConference(null);
    setShowModal(false);
  };

  return (
    <div className="p-4 sm:p-6">
      {/* Search and Filter Controls */}
      <div className="mb-6 space-y-4">
        {/* Search Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <div className="relative w-full sm:w-96">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaSearch className="w-5 h-5 text-gray-500" />
            </div>
            <input
              type="text"
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Search by Conference ID, State, City, or Submitter Name..."
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
            />
          </div>
          <button
            onClick={() => {
              setSearchText("");
              setStartDate("");
              setEndDate("");
              setTodayOnly(false);
            }}
            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            Clear Filters
          </button>
        </div>

        {/* Date Filters */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="todayOnly"
              checked={todayOnly}
              onChange={(e) => setTodayOnly(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="todayOnly" className="text-sm font-medium text-gray-700">
              Today Only
            </label>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">From:</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={todayOnly}
            />
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">To:</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={todayOnly}
            />
          </div>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-red-700">
          Rejected Free Conferences: {filteredConferences.length}
        </h2>
        <div className="flex flex-wrap gap-2">
          <button
            className="flex-1 sm:flex-none bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-2 sm:px-4 sm:py-2 rounded shadow font-semibold transition-colors text-sm sm:text-base min-w-[80px]"
            onClick={() => navigate("/admin/press-conference/free-conference/pending")}
          >
            Pending
          </button>
          <button
            className="flex-1 sm:flex-none bg-green-500 hover:bg-green-600 text-white px-3 py-2 sm:px-4 sm:py-2 rounded shadow font-semibold transition-colors text-sm sm:text-base min-w-[80px]"
            onClick={() => navigate("/admin/press-conference/free-conference/approved")}
          >
            Approved
          </button>
          <button
            className="flex-1 sm:flex-none bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 sm:px-4 sm:py-2 rounded shadow font-semibold transition-colors text-sm sm:text-base min-w-[80px]"
            onClick={() => navigate("/admin/press-conference/free-conference/running")}
          >
            Running
          </button>
          <button
            className="flex-1 sm:flex-none bg-orange-500 hover:bg-orange-600 text-white px-3 py-2 sm:px-4 sm:py-2 rounded shadow font-semibold transition-colors text-sm sm:text-base min-w-[80px]"
            onClick={() => navigate("/admin/press-conference/free-conference/modified")}
          >
            Modified
          </button>
          <button
            className="flex-1 sm:flex-none bg-purple-500 hover:bg-purple-600 text-white px-3 py-2 sm:px-4 sm:py-2 rounded shadow font-semibold transition-colors text-sm sm:text-base min-w-[80px]"
            onClick={() => navigate("/admin/press-conference/free-conference?status=completed")}
          >
            Completed
          </button>
        </div>
      </div>
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500"></div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white rounded-xl shadow-sm border border-gray-100">
            <thead>
              <tr className="bg-gray-100 text-sm text-gray-700">
                <th className="px-4 py-3 text-left">Conference ID</th>
                <th className="px-4 py-3 text-left">Date & Time</th>
                <th className="px-4 py-3 text-left">Location</th>
                <th className="px-4 py-3 text-left">Rejected At</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedConferences.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-8">
                    <div className="flex flex-col items-center">
                      <FaMicrophone className="w-12 h-12 text-gray-300 mb-2" />
                      <p className="text-gray-500">
                        {filteredConferences.length === 0
                          ? (searchText.trim() ? "No rejected conferences match your search criteria." : "No rejected conferences found.")
                          : "No conferences on this page."
                        }
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedConferences.map((conference) => (
                  <tr key={conference._id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-sm">{conference.conferenceId}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center text-sm">
                        <FaCalendarAlt className="mr-1 text-blue-500" />
                        {conference.conferenceDate}
                      </div>
                      <div className="flex items-center text-xs text-gray-500">
                        <FaClock className="mr-1" />
                        {conference.conferenceTime} {conference.timePeriod}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center text-sm">
                        <FaMapMarkerAlt className="mr-1 text-green-500" />
                        {conference.city}, {conference.state}
                      </div>
                      <div className="text-xs text-gray-500">{conference.place}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {conference.adminAction?.actionDate
                        ? new Date(conference.adminAction.actionDate).toLocaleDateString('en-IN')
                        : "N/A"
                      }
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                        Rejected
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleViewDetails(conference)}
                        className="px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-sm flex items-center"
                      >
                        <FaEye className="mr-1" />
                        View Details
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row justify-between items-center mt-6 space-y-4 sm:space-y-0">
          {/* Page Info */}
          <div className="text-sm text-gray-600">
            Showing {((currentPage - 1) * rowsPerPage) + 1} to {Math.min(currentPage * rowsPerPage, filteredConferences.length)} of {filteredConferences.length} conferences
          </div>

          {/* Pagination Navigation */}
          <nav className="inline-flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-4 py-2 rounded border border-blue-400 text-blue-800 bg-white hover:bg-blue-100 transition-colors ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              Previous
            </button>

            {/* Page Numbers */}
            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-3 py-2 rounded border transition-colors ${currentPage === pageNum
                      ? 'border-blue-400 bg-blue-300 text-blue-900 font-bold'
                      : 'border-blue-400 text-blue-800 bg-white hover:bg-blue-100'
                      }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`px-4 py-2 rounded border border-blue-400 text-blue-800 bg-white hover:bg-blue-100 transition-colors ${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              Next
            </button>
          </nav>
        </div>
      )}

      {/* Conference Details Modal */}
      {showModal && selectedConference && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseModal}></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Conference Details - {selectedConference.conferenceId}
                  </h3>
                  <button
                    onClick={handleCloseModal}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <FaTimes className="w-6 h-6" />
                  </button>
                </div>

                {/* Conference Information */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-lg font-semibold text-gray-800 mb-3">Conference Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="font-medium text-gray-600">Topic:</span>
                      <p className="text-gray-900">{selectedConference.topic}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Date & Time:</span>
                      <p className="text-gray-900">{selectedConference.conferenceDate} at {selectedConference.conferenceTime} {selectedConference.timePeriod}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Location:</span>
                      <p className="text-gray-900">{selectedConference.city}, {selectedConference.state}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Venue:</span>
                      <p className="text-gray-900">{selectedConference.place}</p>
                    </div>
                  </div>
                  <div className="mt-3">
                    <span className="font-medium text-gray-600">Purpose:</span>
                    <p className="text-gray-900">{selectedConference.purpose}</p>
                  </div>
                  {selectedConference.landmark && (
                    <div className="mt-3">
                      <span className="font-medium text-gray-600">Nearby Landmark:</span>
                      <p className="text-gray-900">{selectedConference.landmark}</p>
                    </div>
                  )}
                </div>

                {/* Submitter Information */}
                <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                  <h4 className="text-lg font-semibold text-blue-800 mb-3">Submitter Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="font-medium text-blue-600">Name:</span>
                      <p className="text-blue-900">{selectedConference.submittedBy?.name || "N/A"}</p>
                    </div>
                    <div>
                      <span className="font-medium text-blue-600">Email:</span>
                      <p className="text-blue-900">{selectedConference.submittedBy?.email || "N/A"}</p>
                    </div>
                    <div>
                      <span className="font-medium text-blue-600">Organization:</span>
                      <p className="text-blue-900">{selectedConference.submittedBy?.organization || "N/A"}</p>
                    </div>
                    <div>
                      <span className="font-medium text-blue-600">Press Conference ID:</span>
                      <p className="text-blue-900">{selectedConference.submittedBy?.pressConferenceId || "N/A"}</p>
                    </div>
                  </div>
                </div>

                {/* Admin Action Information */}
                <div className="mb-6 p-4 bg-red-50 rounded-lg">
                  <h4 className="text-lg font-semibold text-red-800 mb-3">Rejection Details</h4>
                  <div className="space-y-2">
                    <div>
                      <span className="font-medium text-red-600">Status:</span>
                      <span className="ml-2 px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                        Rejected
                      </span>
                    </div>
                    {selectedConference.adminAction?.actionDate && (
                      <div>
                        <span className="font-medium text-red-600">Rejected At:</span>
                        <p className="text-red-900">{new Date(selectedConference.adminAction.actionDate).toLocaleString('en-IN')}</p>
                      </div>
                    )}
                    {selectedConference.adminAction?.note && (
                      <div>
                        <span className="font-medium text-red-600">Rejection Reason:</span>
                        <p className="text-red-900">{selectedConference.adminAction.note}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Admin Note */}
                {selectedConference.adminNote && (
                  <div className="mb-6 p-4 bg-yellow-50 rounded-lg">
                    <h4 className="text-lg font-semibold text-yellow-800 mb-3">Admin Note</h4>
                    <p className="text-yellow-900">{selectedConference.adminNote}</p>
                  </div>
                )}

                {/* Close Button */}
                <div className="flex justify-end">
                  <button
                    onClick={handleCloseModal}
                    className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RejectedFreeConference;
